// src/components/Inventory/StockEntrySheet.js
// Flujo obrero "+ Inventariar" (§6b): material EXISTENTE elegido por foto →
// solo CANTIDAD + UBICACIÓN de lista. Sin precio/marca/categoría (heredados).
// Escribe vía Cloud Function addStockEntry (permiso + ITC stock.entry) —
// el cliente nunca toca materials/transactions directo en este flujo.
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/firebase-config';
import { t } from '../../lib/i18n';
import { materialSpecLine } from '../../lib/materialCategories';
import { buildMaterialLabelPdf } from '../../lib/materialLabel';
import LocationPicker, { isLocationComplete } from './LocationPicker';

const StockEntrySheet = ({ material, cfg, onClose, onSaved }) => {
    const [quantity, setQuantity] = useState('');
    const [location, setLocation] = useState(null);
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(null);
    // Corrección post-entrada (cantidad ingresada por error) — audit-safe vía adjustStock.
    const [showCorrect, setShowCorrect] = useState(false);
    const [correctQty, setCorrectQty] = useState('');
    const [correcting, setCorrecting] = useState(false);
    const [correctedTo, setCorrectedTo] = useState(null);

    const qty = Math.floor(Number(quantity));
    const canSave = qty >= 1 && isLocationComplete(location) && !busy;

    const applyCorrection = async () => {
        const target = Math.floor(Number(correctQty));
        if (!Number.isFinite(target) || target < 0) { setError(t('correct.invalid')); return; }
        const delta = target - done.qty;   // ajuste compensatorio para llegar a la cantidad correcta
        if (delta === 0) { setShowCorrect(false); return; }
        setError(''); setCorrecting(true);
        try {
            const fn = httpsCallable(functions, 'adjustStock');
            await fn({ materialId: material.id, delta, reason: `Corrección de +Inventariar (${done.qty} → ${target})` });
            setCorrectedTo(target);
            setShowCorrect(false);
            onSaved && onSaved();
        } catch (e) {
            console.error('adjustStock falló:', e);
            setError(e.message || t('correct.error'));
        } finally { setCorrecting(false); }
    };

    const save = async () => {
        setError('');
        setBusy(true);
        try {
            const fn = httpsCallable(functions, 'addStockEntry');
            await fn({ materialId: material.id, quantity: qty, location, notes });
            setDone({ qty, where: location.label || '' });
            onSaved && onSaved();
        } catch (e) {
            console.error('addStockEntry falló:', e);
            setError(e.message || t('stock.error'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-50">
            <div className="bg-white rounded-t-2xl md:rounded-xl shadow-2xl w-full md:max-w-md p-5 space-y-4">
                <div className="flex items-center gap-3">
                    <img src={material.imageUrl || 'https://placehold.co/100x100/eee/ccc?text=N/A'} alt={material.name}
                        className="w-14 h-14 object-cover rounded-md border" />
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{t('stock.title')}</h2>
                        <p className="text-sm text-gray-600">{material.name}</p>
                        {materialSpecLine(material) && (
                            <p className="text-xs text-gray-500">📐 {materialSpecLine(material)}</p>
                        )}
                    </div>
                </div>

                {done ? (
                    <div className="text-center space-y-3 py-4">
                        <p className="text-2xl">✓</p>
                        <p className="font-semibold text-green-700">{done.qty} {t('stock.done')} {done.where}</p>

                        {/* Corregir cantidad ingresada por error (audit-safe: postea un ajuste) */}
                        {correctedTo != null ? (
                            <p className="text-sm text-blue-700">✓ {t('correct.done')} {correctedTo}</p>
                        ) : showCorrect ? (
                            <div className="space-y-2 text-left bg-amber-50 rounded-md p-3">
                                <label className="block text-sm font-medium text-amber-800">{t('correct.label')}</label>
                                <input type="number" inputMode="numeric" min="0" value={correctQty} autoFocus
                                    onChange={(e) => setCorrectQty(e.target.value)}
                                    className="block w-full p-2 text-center border border-gray-300 rounded-md" />
                                {error && <p className="text-xs text-red-600">{error}</p>}
                                <div className="flex gap-2">
                                    <button onClick={() => { setShowCorrect(false); setError(''); }} className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">
                                        {t('remnant.cancel')}
                                    </button>
                                    <button onClick={applyCorrection} disabled={correcting} className="flex-1 px-3 py-2 text-sm font-bold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-40">
                                        {correcting ? t('correct.applying') : t('correct.apply')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button onClick={() => { setShowCorrect(true); setCorrectQty(String(done.qty)); }}
                                className="text-sm text-gray-500 underline hover:text-gray-700">
                                {t('correct.wrong')}
                            </button>
                        )}

                        {/* Pegar la etiqueta en la estiba al momento de contar (§ pedido de campo). */}
                        <button onClick={() => buildMaterialLabelPdf({ material, locationText: location?.label || '' })}
                            className="px-6 py-3 font-bold text-white bg-gray-800 rounded-lg hover:bg-black w-full">
                            🏷 {t('materials.printLabel')}
                        </button>
                        <button onClick={onClose} className="px-6 py-3 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 w-full">
                            {t('remnant.close')}
                        </button>
                    </div>
                ) : (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('stock.quantity')}</label>
                            <input type="number" inputMode="numeric" min="1" value={quantity}
                                onChange={(e) => setQuantity(e.target.value)} autoFocus
                                className="mt-1 block w-full p-3 text-xl text-center border border-gray-300 rounded-md" />
                        </div>
                        <LocationPicker cfg={cfg} value={location} onChange={setLocation} />
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('stock.notes')}</label>
                            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        {error && <p className="text-sm text-red-600">{error}</p>}
                        <div className="flex gap-3">
                            <button onClick={onClose} className="flex-1 px-4 py-3 font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">
                                {t('remnant.cancel')}
                            </button>
                            <button onClick={save} disabled={!canSave}
                                className="flex-1 px-4 py-3 font-bold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-40">
                                {busy ? t('stock.saving') : t('stock.save')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default StockEntrySheet;
